import { createContext, useContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { type PaletteId, type Permission, type Role, type UiThemeId } from '@technohull/shared';
import { api } from '../lib/api.js';

export interface Me {
  user: {
    email: string;
    displayName: string;
    role: Role;
    avatar?: string | null;
    pending?: boolean;
    permissions: Permission[];
    preferences?: {
      theme?: 'light' | 'dark' | 'auto';
      region?: string;
      dateFormat?: string;
      themeChosen?: boolean;
      cursorHalo?: boolean;
      lastSeenAnnId?: number;
      logo?: 'silver' | 'blue' | 'red' | 'green' | 'gold' | 'stock' | 'burgundy';
      hideHeaders?: boolean;
      cellHistory?: boolean;
      hoverTips?: boolean;
      palette?: PaletteId;
      paletteChosen?: boolean;
      uiTheme?: UiThemeId;
    } | null;
  };
  departments: string[];
  calendarUrl?: string;
  /** ids of the dashboard boxes an admin has enabled; the Dashboard renders only these. */
  dashboardBoxes?: string[];
  season: string; // current/default season id
  seasons: string[]; // all selectable season ids
  environment: string;
}

interface AuthValue {
  me: Me | null;
  loading: boolean;
  error: unknown;
  season: string;
  departments: string[];
  can: (p: Permission) => boolean;
  /** retained for API compatibility. visibility is permission-driven, so this is always true. */
  canSeePath: (path: string) => boolean;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<Me>('/api/me'),
    retry: false,
    staleTime: 5 * 60_000,
    // Re-check the session whenever the tab regains focus, so an app left open overnight notices an
    // expired login the next day and re-authenticates on its own (the API client reloads on a 401).
    refetchOnWindowFocus: true,
  });

  const perms = new Set<Permission>(data?.user.permissions ?? []);
  const departments = data?.departments ?? [];
  const value: AuthValue = {
    me: data ?? null,
    loading: isLoading,
    error,
    season: data?.season ?? '',
    departments,
    can: (p) => perms.has(p),
    // page visibility is driven SOLELY by permissions — the same checks the server enforces on every
    // route. A granted role or permission override now always surfaces its page. (Previously this
    // ANDed a separate hand-maintained per-department page whitelist, which silently hid any page a
    // user was granted access to outside their assigned department.)
    canSeePath: () => true,
    logout: async () => {
      const r = await api.post<{ logoutUrl: string }>('/api/logout');
      window.location.href = r.logoutUrl;
    },
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used within <AuthProvider>');
  return v;
}
